import { useEffect, useState } from 'react';
import { Activity, AlertTriangle, Inbox } from 'lucide-react';
import useAnalyticsStore from '../stores/analyticsStore';
import useAnalyticsLiveStream from '../hooks/useAnalyticsLiveStream';
import AnalyticsFilterPanel from '../components/AnalyticsFilterPanel';
import AnalyticsHistogram from '../components/AnalyticsHistogram';
import AnalyticsEventList from '../components/AnalyticsEventList';
import AnalyticsEventDetail from '../components/AnalyticsEventDetail';
import AnalyticsLiveToggle from '../components/AnalyticsLiveToggle';
import '../styles/v2/pages/analytics.css';

// ONVIF analytics event timeline ([6.2]/[6.3]/[6.4]/[6.5]). Page shell composing
// the filter panel, the time-bucket histogram, and the virtualized event list.
// Event detail lands in a later slice.

const Analytics = () => {
  const events = useAnalyticsStore((s) => s.events);
  const loading = useAnalyticsStore((s) => s.loading);
  const error = useAnalyticsStore((s) => s.error);
  const fetchEvents = useAnalyticsStore((s) => s.fetchEvents);
  const fetchCameraNames = useAnalyticsStore((s) => s.fetchCameraNames);
  const clear = useAnalyticsStore((s) => s.clear);

  // Live mode ([6.7]); default OFF. Global page subscribes unscoped.
  const [live, setLive] = useState(false);
  useAnalyticsLiveStream(live);

  useEffect(() => {
    // Global (unscoped) page: clear() first so any lockedCameraId left by the
    // embedded camera section is dropped and all cameras show again.
    clear();
    fetchEvents();
    fetchCameraNames(); // idempotent: guarded by cameraNamesLoaded in the store
  }, [clear, fetchEvents, fetchCameraNames]);

  const showEmpty = !loading && !error && events.length === 0;

  return (
    <div className="analytics-page">
      <div className="analytics-header">
        <div className="analytics-header-left">
          <Activity size={24} className="analytics-header-icon" />
          <div>
            <h1 className="analytics-title">Analytics</h1>
            <p className="analytics-subtitle">ONVIF Event Timeline</p>
          </div>
        </div>
        <div className="analytics-header-actions">
          <AnalyticsLiveToggle live={live} onToggle={setLive} />
        </div>
      </div>

      <AnalyticsFilterPanel />

      <AnalyticsHistogram />

      {error && (
        <div className="analytics-error">
          <AlertTriangle size={18} />
          <span>Failed to load events: {error}</span>
        </div>
      )}

      {!error && showEmpty && (
        <div className="analytics-empty">
          <Inbox size={40} />
          <p>No analytics events to display.</p>
        </div>
      )}

      {!error && !showEmpty && <AnalyticsEventList />}

      <AnalyticsEventDetail />
    </div>
  );
};

export default Analytics;
